import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { listAdminEvents, updateEventModeration } from "@/lib/admin";
import { fmtError } from "@/lib/supabase";
import { coverFor } from "@/lib/defaultEventImages";
import Avatar from "@/components/Avatar";
import ConfirmModal from "@/components/ConfirmModal";
import { Search, Eye, EyeOff, Trash2, Star, StarOff, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const PAGE_SIZE = 25;
const FILTERS = [
  { key: "all", label: "All" },
  { key: "featured", label: "Featured" },
  { key: "hidden", label: "Hidden" },
  { key: "deleted", label: "Deleted" },
];

export default function AdminEvents() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    listAdminEvents({ q, filter, page, pageSize: PAGE_SIZE })
      .then((r) => { setRows(r.rows); setTotal(r.total); })
      .catch((e) => toast.error(fmtError(e)))
      .finally(() => setLoading(false));
  }, [q, filter, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const apply = async (event, patch, label) => {
    try { await updateEventModeration(event.id, patch); toast.success(label); load(); }
    catch (e) { toast.error(fmtError(e)); }
  };

  const toggleHidden = (e) => apply(e, { is_hidden: !e.is_hidden }, e.is_hidden ? "Event un-hidden." : "Event hidden.");
  const toggleFeatured = (e) => apply(e, { is_featured: !e.is_featured }, e.is_featured ? "Un-featured." : "Featured.");
  const softDelete = (e) => setConfirm({
    title: "Soft-delete event?",
    body: "It'll disappear from the public feed and map. Reversible from SQL.",
    confirmLabel: "Soft delete",
    danger: true,
    onConfirm: () => apply(e, { is_deleted: true }, "Event soft-deleted."),
  });

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div className="flex flex-col gap-4">
      {/* Filters + search */}
      <div className="flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[240px] max-w-md">
          <Search size={16} strokeWidth={2.2} className="absolute left-5 top-1/2 -translate-y-1/2 text-ink/45 z-10" />
          <input
            value={q}
            onChange={(e) => { setPage(0); setQ(e.target.value); }}
            placeholder="Search title or city…"
            data-testid="admin-events-search"
            className="chunky-input rounded-full pl-12"
          />
        </div>
        <div className="inline-flex gap-1 p-1 border-[2.5px] border-ink rounded-full bg-paper">
          {FILTERS.map((f) => (
            <button key={f.key}
              data-testid={`filter-${f.key}`}
              onClick={() => { setPage(0); setFilter(f.key); }}
              className={`px-3.5 py-1.5 text-[11px] font-extrabold uppercase tracking-widest rounded-full transition-colors ${
                filter === f.key ? "bg-ink text-paper" : "text-ink/65 hover:text-ink"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="pop p-0 overflow-hidden">
        {loading ? (
          <p className="px-5 py-10 text-center text-ink/55">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="px-5 py-10 text-center text-ink/55">No events.</p>
        ) : (
          <div className="divide-y-2 divide-ink/10">
            {rows.map((e) => (
              <div key={e.id} data-testid={`event-row-${e.id}`} className="flex items-stretch gap-3 p-3">
                <img src={coverFor(e)} alt="" loading="lazy"
                  className="w-20 h-20 sm:w-24 sm:h-24 shrink-0 rounded-2xl object-cover border-[2.5px] border-ink" />
                <div className="flex-1 min-w-0 flex flex-col justify-center gap-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link to={`/events/${e.id}`} className="font-bold text-ink truncate hover:text-lime transition-colors max-w-full">
                      {e.title}
                    </Link>
                    {e.is_featured && <span className="bg-lime text-ink border-2 border-ink rounded-full px-2 text-[9px] font-extrabold uppercase">Featured</span>}
                    {e.is_hidden && <span className="bg-paper text-ink/70 border-2 border-ink rounded-full px-2 text-[9px] font-extrabold uppercase">Hidden</span>}
                    {e.is_deleted && <span className="bg-paper text-destructive border-2 border-destructive rounded-full px-2 text-[9px] font-extrabold uppercase">Deleted</span>}
                  </div>
                  <p className="text-ink/55 text-xs truncate">
                    {e.category} · {e.city} · {e.attendees_count} going · {new Date(e.created_at).toLocaleDateString()}
                  </p>
                  <div className="inline-flex items-center gap-2 text-xs text-ink/65">
                    <Avatar src={e.host_avatar_url} name={e.host_name} size="xs" />
                    {e.host_username ? `@${e.host_username}` : e.host_name}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-1.5 self-center">
                  <button title={e.is_featured ? "Un-feature" : "Feature"} data-testid={`btn-feature-${e.id}`}
                    onClick={() => toggleFeatured(e)}
                    className="inline-flex items-center justify-center w-9 h-9 border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime transition-colors">
                    {e.is_featured ? <StarOff size={14} strokeWidth={2.2} /> : <Star size={14} strokeWidth={2.2} />}
                  </button>
                  <button title={e.is_hidden ? "Un-hide" : "Hide"} data-testid={`btn-hide-${e.id}`}
                    onClick={() => toggleHidden(e)}
                    className="inline-flex items-center justify-center w-9 h-9 border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime transition-colors">
                    {e.is_hidden ? <Eye size={14} strokeWidth={2.2} /> : <EyeOff size={14} strokeWidth={2.2} />}
                  </button>
                  <button title="Soft delete" data-testid={`btn-delete-${e.id}`}
                    onClick={() => softDelete(e)}
                    className="inline-flex items-center justify-center w-9 h-9 border-[2.5px] border-ink rounded-full bg-paper hover:bg-destructive hover:text-paper transition-colors">
                    <Trash2 size={14} strokeWidth={2.2} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button disabled={page === 0} onClick={() => setPage(page - 1)}
            className="inline-flex items-center justify-center w-10 h-10 border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime disabled:opacity-40 transition-colors">
            <ChevronLeft size={16} strokeWidth={2.5} />
          </button>
          <span className="text-sm font-bold text-ink/70">Page {page + 1} / {totalPages}</span>
          <button disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}
            className="inline-flex items-center justify-center w-10 h-10 border-[2.5px] border-ink rounded-full bg-paper hover:bg-lime disabled:opacity-40 transition-colors">
            <ChevronRight size={16} strokeWidth={2.5} />
          </button>
        </div>
      )}

      <ConfirmModal
        open={!!confirm}
        title={confirm?.title}
        body={confirm?.body}
        confirmLabel={confirm?.confirmLabel || "Confirm"}
        danger={confirm?.danger}
        onCancel={() => setConfirm(null)}
        onConfirm={async () => { await confirm?.onConfirm?.(); setConfirm(null); }}
      />
    </div>
  );
}
